import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { dashboardService } from '../../services/DashboardService';
import { Loader2, Calendar, Activity } from 'lucide-react';

type TimeBucket = 'day' | 'month' | 'year';

interface ChartData {
    period: string;
    count: number;
}

const KPITimeseriesChart: React.FC = () => {
    const [data, setData] = useState<ChartData[]>([]);
    const [bucket, setBucket] = useState<TimeBucket>('month');
    const [isLoading, setIsLoading] = useState(true);

    const loadData = async (selectedBucket: TimeBucket) => {
        setIsLoading(true);
        try {
            // Lógica de fechas
            let monthsToLookBack = 12; // Por defecto 1 año (para meses)
            if (selectedBucket === 'day') {
                monthsToLookBack = 1; // 1 mes para días (30 barras)
            } else if (selectedBucket === 'year') {
                monthsToLookBack = 60; // 5 años para años
            }

            const rawData = await dashboardService.getKPISeries(selectedBucket, monthsToLookBack);

            // Mapeo y formateo amigable para el UX
            const formattedData = rawData.map((item: any) => {
                const date = new Date(item.period);
                let label = '';

                if (selectedBucket === 'day') {
                    label = date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                } else if (selectedBucket === 'month') {
                    label = date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
                } else {
                    label = date.getFullYear().toString();
                }

                return {
                    period: label.toUpperCase(),
                    count: item.count
                };
            });

            setData(formattedData);
        } catch (error) {
            console.error('Error loading KPI series:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(bucket);
    }, [bucket]);

    // Calcular el valor máximo para destacar la barra principal
    const maxCount = data.length > 0 ? Math.max(...data.map(d => d.count)) : 0;

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-zinc-950/90 backdrop-blur-md border border-zinc-800 p-3 shadow-xl rounded-sm">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">{label}</p>
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                        <Activity size={14} className="text-zinc-400" />
                        {payload[0].value} Consultas
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden mb-10">
            <div className="p-4 border-b border-zinc-800 bg-zinc-950/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-white flex items-center">
                        <Calendar size={14} />
                    </span>
                    <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-zinc-400">
                        Histórico de Consultas WhatsApp
                    </h3>
                </div>

                {/* Controles de Bucket */}
                <div className="flex bg-zinc-950 rounded-sm p-1 border border-zinc-800">
                    <button
                        onClick={() => setBucket('day')}
                        className={`text-[10px] uppercase font-bold tracking-widest px-4 py-1.5 rounded-sm transition-all ${bucket === 'day' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Diario
                    </button>
                    <button
                        onClick={() => setBucket('month')}
                        className={`text-[10px] uppercase font-bold tracking-widest px-4 py-1.5 rounded-sm transition-all ${bucket === 'month' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Mensual
                    </button>
                    <button
                        onClick={() => setBucket('year')}
                        className={`text-[10px] uppercase font-bold tracking-widest px-4 py-1.5 rounded-sm transition-all ${bucket === 'year' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Anual
                    </button>
                </div>
            </div>

            <div className="p-6 h-[300px] relative flex flex-col">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm z-20 transition-opacity">
                        <Loader2 className="h-8 w-8 text-zinc-500 animate-spin" />
                    </div>
                )}

                {data.length === 0 && !isLoading && (
                    <div className="flex-1 flex items-center justify-center text-zinc-600 text-xs italic">
                        No hay suficientes datos históricos.
                    </div>
                )}

                {data.length > 0 && (
                    <div className="flex-1 w-full h-full min-h-[200px]">
                        <ResponsiveContainer width="99%" height="100%">
                            <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <XAxis
                                    dataKey="period"
                                    stroke="#52525b"
                                    fontSize={10}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fill: '#71717a' }}
                                    dy={10}
                                />
                                <YAxis
                                    stroke="#52525b"
                                    fontSize={10}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(value) => value === 0 ? '' : value}
                                />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                                />
                                <Bar
                                    dataKey="count"
                                    radius={[2, 2, 0, 0]}
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                >
                                    {data.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.count === maxCount && maxCount > 0 ? '#ffffff' : '#3f3f46'}
                                            className="transition-all duration-300 hover:opacity-80"
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KPITimeseriesChart;
